/**
 * @ Author: Kamil Michalak (k.michalak@kstudio.pl)
 * @ Create Time: 2019-09
 * @ Modified by: Kamil Michalak (k.michalak@kstudio.pl)
 * @ Modified time: 2019-11
 */

import { observable, makeObservable } from "mobx"
import { TraxisModelAbstract } from "store/api/TraxisModelAbstract"
import { makeMapCollection } from "store/ModelTools"
//import { TvGuideConf } from "store/page/TvGuide"
//import { rootStore } from "store/RootStore"
//import { DayInSec } from "store/util/moment"
import { TraxisAbstract } from "./TraxisAbstract"
import { TraxisConditionAnd, TraxisConditionOr } from "./TraxisCondition"
import { TvGuideConf } from "store/page/TvGuide.js"
import { moment } from "store/util/moment"

export const RECORDINGS_STATUS = {}

export class GetRecordingsMarkersModel extends TraxisModelAbstract {
	/**
	 * @type {Map.<GetRecordingsMarkersItem>}
	 */
	_$Recording = new Map()
	// There are cases where recorded series are in the 'Suspended' state
	_$SuspendedSeries = new Map()
	resultCount = null

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			_$Recording: observable.shallow,
			resultCount: observable
		})
	}

	set Recording(v) {
		// MTVW-820: filter FactoryState "Suspended" to avoid duplicate entries
		this.setReady(false)
		makeMapCollection(this._$Recording, v, i => (i.EventId ? i.EventId : i.FactoryState !== "Suspended" ? i.SeriesId : "ignore"), () => new GetRecordingsMarkersItem())
		makeMapCollection(this._$SuspendedSeries, v, i => (i.SeriesId && i.FactoryState === "Suspended" ? i.SeriesId : "ignore"), () => new GetRecordingsMarkersItem())
		this.setReady(true)
		return this._$Recording
	}

	/**
	 * @returns {Map.<GetRecordingsMarkersItem>}
	 */
	get Recording() {
		return this._$Recording
	}

	get SuspendedSeries() {
		return this._$SuspendedSeries
	}

	/**
	 * @returns {GetRecordingsMarkersItem}
	 */
	getById(id) {
		return this.Recording.get(id) ?? null
	}

	getSuspendedSeriesBy(id) {
		return this.SuspendedSeries.get(id) ?? null
	}
}

export class GetRecordingsMarkersItem {
	id = null
	EventId = null
	ParentRecordingCount = 0
	State = null
	IsPersonal = null
	SeriesId = null
	// String (Active, Suspended, Completed)
	FactoryState = null
	ProfileId = null
	// MTVW-820: removed AvailabilityStart,AvailabilityEnd, it has a huge time impact
	//AvailabilityStart = null
	//AvailabilityEnd = null
	IsDeleteProtected = false

	constructor() {
		makeObservable(this, {
			EventId: observable,
			ParentRecordingCount: observable,
			State: observable,
			IsPersonal: observable,
			SeriesId: observable,
			FactoryState: observable,
			ProfileId: observable,
			//AvailabilityStart: observable,
			//AvailabilityEnd: observable,
			IsDeleteProtected: observable,
		})
	}

	static createItem() {
		const item = new GetRecordingsMarkersItem()
		return item
	}

	get isSeries() {
		return this.ParentRecordingCount !== 0 || this.SeriesId !== null
	}

	get TitleId() {
		return this.EventId?.split(",")[0]
	}

	get isComplete() {
		return this.State === "Complete"
	}

	get isDeleted() {
		return this.State === "Deleted"
	}

	get recType() {
		const t = ["rec", this.isSeries ? "series" : "single"]
		if (this.isComplete) {
			t.push("complete")
		} else {
			t.push("planned")
		}
		return t.join("_")
	}
}

/**
 * @property {array<string>} DataMaps
 */
export class GetRecordingsMarkers extends TraxisAbstract {
	_onStartCreate = true

	get _model() {
		return GetRecordingsMarkersModel
	}

	_getParseResp(data) {
		return data.Recordings
	}
	getXmlReq() {
		//console.debug("GetRecordingsMarkers getXmlReq")
		if (this.Data) this.Data.isReady = false
		const filter = TraxisConditionAnd()
			.setAsFilterOption()
			.add(
				TraxisConditionOr(
					TraxisConditionAnd()
						.add("Type==Event")
						// MTVW-820: shouldn't have any impact
						.add("Channels>0")
						/* MTVW-820: removed Deleted (big repsonse time impact), added Pending and Failed */
						.add("(State==Complete||State==Scheduled||State==Pending||State==Recording||State==Failed)")
					// MTVW-820: time condition for better performance
					// DO NOT USE BECAUSE OF FUTURE AND PAST RECORDINGS
					//.add(`StartTime>=${moment().utc().subtract(TvGuideConf.dayScopePrev + 2, "days").format("YYYY-MM-DD")}`)
					//.add(`EndTime<=${moment().utc().add(TvGuideConf.dayScopeNext + 2, "days").format("YYYY-MM-DD")}`)
				).add(TraxisConditionAnd().add("Type==Series")
					.add("Channels>0")
					//.add("ChildRecordings>0")
					//.add("(FactoryState!=Suspended)")
				)
			)
			.add(
				TraxisConditionOr()
					.add("IsPersonal==false")
					.add(`ProfileId=="${this.profileId}"`)
			)
		// <Option type="Propset">all</Option>
		//<Option type="Props">EventId,SeriesId,State,ParentRecordingCount,IsPersonal,FactoryState</Option>
		//${filter}
		return `<?xml version="1.0" encoding="utf-8"?>
<Request xmlns="urn:eventis:traxisweb:1.0">
	<Identity>
		<ProfileId>${this.profileId}</ProfileId>
	</Identity>
	<RootRelationQuery relationName="Recordings">
		<Options>
			${/* EventId,SeriesId,State,ParentRecordingCount,IsPersonal,FactoryState,UpdateTime,NextScheduledStartTime */ ""}
			${/* MTVW-820: removed AvailabilityStart,AvailabilityEnd, it has a huge time impact */ ""}
			<Option type="Props">EventId,SeriesId,State,ParentRecordingCount,IsPersonal,FactoryState,ProfileId,IsDeleteProtected</Option>
			${filter}
		</Options>
			${/* MTVW-820: performance optimization with ChildRecordings */ ""}
			<SubQueries>
            <SubQuery relationName="ChildRecordings">
                <Options>
                    <Option type="filter"><![CDATA[(State==Complete||State==Scheduled||State==Pending||State==Recording||State==Failed)]]></Option>
                    <Option type="Sort">~StartTime</Option>
                </Options>
            </SubQuery>
        </SubQueries>
  </RootRelationQuery>
</Request>`
	}
}
